import { ROTA_APIS } from "@/api_framework/api_config";
import { IRotaOverbookingBulkUploadResponse } from "@/api_framework/api_modals/Rota";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import axios, { AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useConfiguration } from "../../useConfiguration";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import { useAPIUtils } from "../../useAPIUtils";

AxiosCurlirize(axios, () => {});

const extractFileNameFromDisposition = (contentDisposition?: string) => {
  if (!contentDisposition) return "";

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].replace(/"/g, "").trim());

  const filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
  if (filenameMatch?.[1]) return filenameMatch[1].replace(/"/g, "").trim();

  return "";
};

const triggerDownload = (blobData: Blob, fileName: string) => {
  const downloadUrl = window.URL.createObjectURL(blobData);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

export const useRotaOverbookingBulkUploadAPI = () => {
  const configuration = useConfiguration();
  const dispatch = useAppDispatch();
  const { sendCurl } = useCurlLogger();
  const { captureError } = useAPIUtils();

  const execute = useCallback(
    async (file: File | Blob): Promise<IRotaOverbookingBulkUploadResponse> => {
      dispatch(setLoading({ key: "isOverbookingBulkUploadInProgress", status: true }));
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const formData = new FormData();
        formData.append("file", file);

        const res: AxiosResponse<Blob> = await axios.post(
          ROTA_APIS?.ROTA_OVERBOOKING_BULK_UPLOAD?.baseURL ?? "",
          formData,
          {
            responseType: "blob",
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );

        const contentType = `${res.headers?.["content-type"] ?? ""}`.toLowerCase();
        const contentDisposition =
          (res.headers?.["content-disposition"] as string) || (res.headers?.["Content-Disposition"] as string);
        const isJSONResponse = contentType.includes("application/json");

        if (isJSONResponse) {
          const parsedResponse = JSON.parse(await res.data.text()) as IRotaOverbookingBulkUploadResponse;

          if (parsedResponse?.status === true) {
            toast.success(parsedResponse.message ?? "Bulk upload completed");
            return parsedResponse;
          }

          toast.error(parsedResponse?.message ?? "Bulk upload failed");
          return parsedResponse;
        }

        const fileName = extractFileNameFromDisposition(contentDisposition) || "overbooking_bulk_upload_response.xlsx";
        triggerDownload(res.data, fileName);
        toast.success("File downloaded successfully");
        return {
          status: true,
          message: "File downloaded successfully",
          data: null,
        };
      } catch (error) {
        if (axios.isAxiosError(error)) {
          sendCurl?.(error.config?.["curlCommand"]);
          captureError(error);

          if (error.response?.data instanceof Blob) {
            try {
              const errorPayload = JSON.parse(await error.response.data.text()) as IRotaOverbookingBulkUploadResponse;
              return {
                status: false,
                message: errorPayload?.message ?? "Failed to upload overbooking requests",
                data: errorPayload?.data ?? null,
              };
            } catch {
              // Fall through to default error handling.
            }
          }

          return {
            status: false,
            message: error.response?.data?.message ?? "Failed to upload overbooking requests",
            data: null,
          };
        }

        const err = error as Error;
        toast.error("Server Error: " + err.message);
        return {
          status: false,
          message: err.message,
          data: null,
        };
      } finally {
        dispatch(setLoading({ key: "isOverbookingBulkUploadInProgress", status: false }));
      }
    },
    [captureError, configuration, dispatch, sendCurl],
  );

  return execute;
};
